import { useScopedAtomValue } from '@lemon8/web-app-shared/ssr';
import { FC, useCallback } from 'react';
import { ImmersivePostItemAtoms } from '../../atoms';
import { Link } from '@remix-run/react';
import { useAtomValue } from 'jotai';
import { $Locale } from '@lemon8/web-app-shared/atom';
import { useTranslation } from 'react-i18next';
import { useNumFormatter } from '~/utils/format';
import ChevronRightIcon from 'icon/chevron_right.svg';
import _groupBy from 'lodash/groupBy';
import { useViewTracking } from '~/utils/use-view-tracking';
import { ModuleType } from '~/utils/module-type';
import { ClickPosition, ClickPositionPrefix } from '~/utils/click-position';
import { useActiveToArticle } from '~/utils/active/use-active-to-article';
import { CommentUtil } from 'shared/models';
import { useSEOLinkBuilder } from '~/utils/build-seo-url';
import { AdditionalParamsOptionalProps, PositionPrefixOptionalProps } from '../../shared/typing';

import './style.scss';

export const ArticleComments: FC<PositionPrefixOptionalProps & AdditionalParamsOptionalProps> = ({
  positionPrefix = ClickPositionPrefix.none,
  additionalParams = {},
}) => {
  const { t } = useTranslation();
  const formatNum = useNumFormatter();
  const article = useScopedAtomValue(ImmersivePostItemAtoms.$CurrentArticle);
  const comments = useScopedAtomValue(ImmersivePostItemAtoms.$CurrentComments);
  const activeToArticle = useActiveToArticle(article);
  const { commentCount } = article;
  const buildSEOLink = useSEOLinkBuilder();

  const { primary = [], reply = [] } = _groupBy(
    comments.filter((comment) => comment.text),
    (comment) => (CommentUtil.isPrimaryComment(comment) ? 'primary' : 'reply'),
  );

  const countOfCommentsToShow = 2;
  const commentsToShow = [...primary, ...reply].slice(0, countOfCommentsToShow);

  const commentObservedRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_page_module: positionPrefix + ModuleType.related_article,
        current_position: positionPrefix + ClickPosition.comment,
        group_id: article.groupId,
        ...additionalParams,
      });
    }, []),
  });

  const handleCommentClick = useCallback(() => {
    window.Tea('wap_click', {
      current_position: positionPrefix + ClickPosition.comment,
      is_cta: 1,
      group_id: article.groupId,
      ...additionalParams,
    });
    activeToArticle([positionPrefix, ClickPosition.comment]);
  }, [activeToArticle]);

  const commentSeeMoreObservedRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_page_module: positionPrefix + ModuleType.related_article,
        current_position: positionPrefix + ClickPosition.comment_see_more,
        group_id: article.groupId,
        ...additionalParams,
      });
    }, []),
  });

  const handleCommentSeeMoreClick = useCallback(() => {
    window.Tea('wap_click', {
      current_position: positionPrefix + ClickPosition.comment_see_more,
      is_cta: 1,
      group_id: article.groupId,
      ...additionalParams,
    });
    activeToArticle([positionPrefix, ClickPosition.comment_see_more]);
  }, [activeToArticle]);

  if (!commentsToShow.length) {
    return null;
  }

  return (
    <div className="article-comments">
      <div className="comments" ref={commentObservedRef} onClick={handleCommentClick}>
        {commentsToShow.map((comment) => (
          <div className="comment-item" key={comment.commentId}>
            <p>
              <object>
                <Link
                  to={buildSEOLink(CommentUtil.getAuthorPath(comment))}
                  className="author-name"
                  onClick={(e) => e.preventDefault()}
                >{`${comment.author.nickName}: `}</Link>
              </object>
              {comment.text}
            </p>
          </div>
        ))}
      </div>
      <p className="view-all-comments" ref={commentSeeMoreObservedRef} onClick={handleCommentSeeMoreClick}>
        {commentCount > countOfCommentsToShow
          ? t('seo_keyword_page_viewComments_moreThanTwo').replace('%s', formatNum(commentCount))
          : t('seo_keyword_page_viewComments_all')}
        <ChevronRightIcon />
      </p>
    </div>
  );
};
